import { useMemo, useState } from 'react';
import { Box, Button, Divider, Fab, IconButton, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { EmptyState } from '../../../shared/components/EmptyState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useAuth } from '../../../shared/auth/useAuth';
import { timesheetEntriesHooks, useTimesheetEntriesList, useTimesheetEntryTotals } from '../hooks';
import { timesheetEntriesApi } from '../api';
import { TimesheetEntryFormDialog } from '../TimesheetEntryFormDialog';
import { LancamentoCard } from '../components/LancamentoCard';
import { ResumoHorasDialog } from '../components/ResumoHorasDialog';
import { groupByDay, sumHoras } from '../utils';
import type {
  TimesheetEntryDto,
  TimesheetEntryListItem,
  TimesheetEntryTotalsFilters,
} from '../types';

interface MonthRange {
  ano: number;
  mes: number; // 1..12
  dataInicial: string;
  dataFinal: string;
  label: string;
}

function buildMonth(ref: dayjs.Dayjs): MonthRange {
  const start = ref.startOf('month');
  const raw = start.locale('pt-br').format('MMMM [de] YYYY');
  return {
    ano: start.year(),
    mes: start.month() + 1,
    dataInicial: start.format('YYYY-MM-DD'),
    dataFinal: start.endOf('month').format('YYYY-MM-DD'),
    label: raw.charAt(0).toUpperCase() + raw.slice(1),
  };
}

export function MeusApontamentos(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const lockedColabId = user?.externalId ?? null;

  const [ref, setRef] = useState<dayjs.Dayjs>(() => dayjs().startOf('month'));
  const month = useMemo(() => buildMonth(ref), [ref]);

  const [editing, setEditing] = useState<TimesheetEntryDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<TimesheetEntryListItem | null>(null);
  const [resumoOpen, setResumoOpen] = useState(false);

  const deleteMutation = timesheetEntriesHooks.useDelete();

  const filters: TimesheetEntryTotalsFilters = useMemo(
    () => ({
      colaboradorCodigo: lockedColabId,
      dataInicial: month.dataInicial,
      dataFinal: month.dataFinal,
    }),
    [lockedColabId, month.dataInicial, month.dataFinal],
  );

  // pageSize alto: um mês raramente passa de 200 lançamentos de um único colaborador.
  const listQuery = useTimesheetEntriesList(
    { page: 1, pageSize: 200, sortBy: 'data', sortOrder: 'desc' },
    filters,
  );
  const totals = useTimesheetEntryTotals(filters);

  const rows = listQuery.data?.data ?? [];
  const grouped = useMemo(() => groupByDay(rows), [rows]);

  async function openEdit(row: TimesheetEntryListItem): Promise<void> {
    try {
      setEditing(await timesheetEntriesApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.apontamentos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          position: 'sticky',
          top: 56,
          // Acima do conteúdo, mas abaixo do AppBar e da BottomNav (que usam appBar).
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton
            aria-label={t('features.apontamentos.meus.prevMonth')}
            onClick={() => setRef((r) => r.subtract(1, 'month').startOf('month'))}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ flex: 1, textAlign: 'center' }} aria-live="polite">
            {month.label}
          </Typography>
          <IconButton
            aria-label={t('features.apontamentos.meus.nextMonth')}
            onClick={() => setRef((r) => r.add(1, 'month').startOf('month'))}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" alignItems="flex-end" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t('features.apontamentos.totals.total')}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              sx={{ fontFamily: 'monospace', lineHeight: 1 }}
            >
              {totals.isLoading ? '—' : (totals.data?.totalHoras ?? '00:00')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon />}
            onClick={() => setResumoOpen(true)}
            disabled={lockedColabId == null}
          >
            {t('features.apontamentos.actions.resumo')}
          </Button>
        </Stack>
      </Paper>

      {grouped.length === 0 && !listQuery.isLoading ? (
        <EmptyState description={t('features.apontamentos.meus.empty')} />
      ) : (
        grouped.map((group) => (
          <Box key={group.key} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
              <Typography variant="subtitle2">
                {(() => {
                  const s = dayjs(group.date).locale('pt-br').format('ddd, DD/MM');
                  return s.charAt(0).toUpperCase() + s.slice(1);
                })()}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Divider />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {sumHoras(group.entries)}
              </Typography>
            </Stack>
            {group.entries.map((e) => (
              <LancamentoCard
                key={e.id}
                entry={e}
                onEdit={(row) => void openEdit(row)}
                onDelete={(row) => setPendingDelete(row)}
              />
            ))}
          </Box>
        ))
      )}

      <Fab
        color="primary"
        aria-label={t('features.apontamentos.actions.new')}
        onClick={() => setEditing('new')}
        // 56 = altura padrão da BottomNavigation; 16 = gap visual mínimo.
        sx={{ position: 'fixed', right: 16, bottom: `calc(56px + ${16}px)` }}
      >
        <AddIcon />
      </Fab>

      <ResumoHorasDialog
        open={resumoOpen}
        onClose={() => setResumoOpen(false)}
        colaboradorCodigo={lockedColabId}
        initialDate={month.dataInicial}
      />

      <TimesheetEntryFormDialog
        open={editing !== null}
        entry={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.apontamentos.deleteConfirm.title')}
        description={t('features.apontamentos.deleteConfirm.description')}
        confirmLabel={t('features.apontamentos.actions.confirmDelete')}
        loading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
}
